import { ExperimentsProvider } from '@magpie/ui-experiments';
import type { ComponentProps, Dispatch, FC, ReactNode, SetStateAction } from 'react';
import { useContext, useEffect, useState } from 'react';
import { WidgetSettingsProvider } from '..';

import { useApi } from '../../hooks';
import { setExperiments as setWindowExperiments } from '../../utils/windowGlobals';

// TODO: Add api prop and fetch functionality to ui-experiment/ExperimentsProvider

type ExperimentsMap = Record<string, unknown>;
type ExperimentsProviderExperiments = ComponentProps<typeof ExperimentsProvider>['experiments'];

interface Props {
    children: ReactNode;
}

/* istanbul ignore next */
class Experiments {
    private $debug: boolean;

    private $setDebug: Dispatch<SetStateAction<boolean>>;

    private $experiments: ExperimentsMap;

    private $setExperiments: Dispatch<SetStateAction<ExperimentsMap>>;

    private $isFetchPending: boolean;

    private $setIsFetchPending: Dispatch<SetStateAction<boolean>>;

    constructor(
        debug: boolean,
        setDebug: Dispatch<SetStateAction<boolean>>,
        experiments: ExperimentsMap,
        setExperiments: Dispatch<SetStateAction<ExperimentsMap>>,
        isFetchPending: boolean,
        setIsFetchPending: Dispatch<SetStateAction<boolean>>,
    ) {
        this.$debug = debug;
        this.$setDebug = setDebug;
        this.$experiments = experiments;
        this.$setExperiments = setExperiments;
        this.$isFetchPending = isFetchPending;
        this.$setIsFetchPending = setIsFetchPending;
    }

    public get debug() {
        return this.$debug;
    }

    public set debug(value: boolean) {
        this.$setDebug(value);
    }

    public get experiments() {
        return this.$experiments;
    }

    public set experiments(value: ExperimentsMap) {
        this.$setExperiments(value);
    }

    public get isFetchPending() {
        return this.$isFetchPending;
    }

    public set isFetchPending(value: boolean) {
        this.$setIsFetchPending(value);
    }
}

export const ExperimentationProvider: FC<Props> = ({ children = undefined }) => {
    const [debug, setDebug] = useState(false);
    const configuration = useContext(WidgetSettingsProvider.Context);
    const [experiments, setExperiments] = useState<ExperimentsMap>({});
    const [isFetchPending, setIsFetchPending] = useState(true);
    const API = useApi();

    // For testing, type window.experiments.debug = true into the browser console to activate debug mode.
    // You can also force an experiment with window.experiments.experiments = { ... }
    useEffect(() => {
        /* istanbul ignore next */
        setWindowExperiments(
            new Experiments(
                debug,
                setDebug,
                experiments,
                setExperiments,
                isFetchPending,
                setIsFetchPending,
            ),
        );
    }, [debug, experiments, isFetchPending]);

    useEffect(() => {
        if (configuration?.disableExperimentsTreeCall) return;

        void API.getExperimentsTree()
            .then((data) => {
                setExperiments(data);
            })
            .catch(() => {
                // If there's an error here the service is either down or there are no flags in the ExpressCheckout namespace.
                // In which case we'll just carry on as normal and experiments will be set to their defaults.
            })
            .finally(() => {
                setIsFetchPending(false);
            });
    }, []);

    return (
        <ExperimentsProvider
            debug={debug}
            experiments={experiments as ExperimentsProviderExperiments}
            isFetchPending={isFetchPending}
        >
            {children}
        </ExperimentsProvider>
    );
};
